import React from 'react'
import { PROJECT } from 'app-constants'
import { capitaliseFirstLetter } from 'helpers/utils'
import useProject from 'hooks/useProject'
import ActionDialog from 'components/dialog/action'

export type HubAction = 'deactivate' | 'reactivate' | 'delete'

interface ActionCopy {
  title: string
  description: string
  confirmLabel: string
  /** What to type before the action can be confirmed, if anything. */
  phrase?: string
  /** Whether the phrase can be copied. Permanent deletion asks for it typed out, as a last check. */
  copyable?: boolean
  danger: boolean
}

const copyFor = (action: HubAction, name: string): ActionCopy => {
  const hub = capitaliseFirstLetter(PROJECT)
  if (action === 'deactivate')
    return {
      title: `Deactivate ${name}?`,
      description: `Once a ${PROJECT} is deactivated, documents and tables cannot be viewed or edited. Syncs are paused, and the member list and settings cannot be updated.`,
      confirmLabel: `Deactivate ${hub}`,
      phrase: name,
      copyable: true,
      danger: true
    }
  if (action === 'reactivate')
    return {
      title: `Reactivate ${name}?`,
      description: `Documents and tables can be viewed and edited again, and syncs resume.`,
      confirmLabel: `Reactivate ${hub}`,
      danger: false
    }
  return {
    title: `Permanently delete ${name}?`,
    description: `Deletes this ${PROJECT}'s tables, documents, views and audit history. This can't be undone, so back up anything you need first.`,
    confirmLabel: `Permanently Delete ${hub}`,
    phrase: `I want to delete ${name}`,
    danger: true
  }
}

/**
 * Deactivates, reactivates or permanently deletes the hub, in our dialog: what the action does and, for the
 * two that stop the hub, a phrase to type before it can be confirmed (the hub's name, to copy; or, to
 * delete, a sentence typed out). `onConfirm` rejects, having said why, if it fails, and the dialog stays
 * open to try again.
 */
const HubActionDialog: React.FC<{ action: HubAction; onClose: () => void; onConfirm: () => Promise<void> }> = ({
  action,
  onClose,
  onConfirm
}) => {
  const { project } = useProject()
  return <ActionDialog {...copyFor(action, project.name)} onClose={onClose} onConfirm={onConfirm} />
}

export default HubActionDialog
